import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Play, Clock, BookOpen, Star, ChevronLeft, Share2, Target, Users, Award, Zap, CheckCircle, ChevronRight } from 'lucide-react'
import { courses } from '../data/mockData'
import useStore from '../store/useStore'

// Learning Paths data matching the style from LearningPathsList
const learningPaths = [
  {
    id: 1,
    title: "AI & Machine Learning Mastery",
    description: "From fundamentals to advanced AI applications and real-world implementations. Build and deploy intelligent systems.",
    category: "Technology & AI",
    courseCount: 5,
    totalDuration: "14 weeks",
    rating: 4.9,
    color: "from-blue-500 to-purple-600",
    image: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=800&h=400&fit=crop",
    studentCount: 2500,
    level: "Beginner to Advanced",
    prerequisites: ["Basic programming knowledge", "High school mathematics"],
    outcomes: [
      "Build and train neural networks",
      "Understand ML algorithms", 
      "Deploy AI models to production",
      "Work with real-world datasets"
    ],
    modules: [
      {
        title: "AI Fundamentals",
        description: "Introduction to artificial intelligence concepts and history",
        duration: "3 weeks",
        lessons: 12
      },
      {
        title: "Machine Learning Core",
        description: "Supervised and unsupervised learning algorithms",
        duration: "4 weeks",
        lessons: 16
      },
      {
        title: "Deep Learning",
        description: "Neural networks and advanced architectures",
        duration: "4 weeks", 
        lessons: 18
      },
      {
        title: "AI Projects",
        description: "Real-world AI implementation and deployment",
        duration: "3 weeks",
        lessons: 14
      }
    ],
    courses: [101, 102],
    certificate: true
  },
  {
    id: 2,
    title: "Web Development Bootcamp",
    description: "Build modern web applications with React and full-stack technologies from scratch. Master the complete development lifecycle.",
    category: "Technology & AI",
    courseCount: 4,
    totalDuration: "12 weeks",
    rating: 4.8,
    color: "from-green-500 to-teal-600",
    image: "https://images.unsplash.com/photo-1627398242454-45a1465c2479?w=800&h=400&fit=crop",
    studentCount: 3200,
    level: "Beginner to Intermediate",
    prerequisites: ["Basic HTML/CSS knowledge", "Logical thinking"],
    outcomes: [
      "Build responsive web applications",
      "Master React and modern frameworks",
      "Understand backend development",
      "Deploy applications to cloud"
    ],
    modules: [
      {
        title: "Frontend Fundamentals",
        description: "HTML, CSS, and JavaScript essentials",
        duration: "3 weeks",
        lessons: 14
      },
      {
        title: "React Development",
        description: "Modern React with hooks and context",
        duration: "4 weeks",
        lessons: 16
      },
      {
        title: "Backend Integration",
        description: "Node.js, Express, and database management",
        duration: "3 weeks",
        lessons: 12
      },
      {
        title: "Full-stack Project",
        description: "Build and deploy a complete web application",
        duration: "2 weeks",
        lessons: 8
      }
    ],
    courses: [103, 101],
    certificate: true
  },
  {
    id: 3,
    title: "Startup Entrepreneurship",
    description: "Launch and scale your business idea with proven strategies and frameworks. Learn from successful founders.",
    category: "Business & Entrepreneurship", 
    courseCount: 4,
    totalDuration: "10 weeks",
    rating: 4.7,
    color: "from-orange-500 to-red-600",
    image: "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&h=400&fit=crop",
    studentCount: 1800,
    level: "All Levels",
    prerequisites: ["Business idea (optional)", "Basic computer skills"],
    outcomes: [
      "Validate business ideas effectively",
      "Create business plans and pitch decks",
      "Understand funding and marketing",
      "Launch and scale your startup"
    ],
    modules: [
      {
        title: "Idea Validation",
        description: "Market research and problem-solution fit",
        duration: "2 weeks",
        lessons: 8
      },
      {
        title: "Business Planning",
        description: "Business models, financials, and strategy",
        duration: "3 weeks",
        lessons: 12
      },
      {
        title: "Funding Strategies",
        description: "Pitching, investors, and bootstrapping",
        duration: "3 weeks",
        lessons: 10
      },
      {
        title: "Growth & Scaling",
        description: "Marketing, team building, and operations",
        duration: "2 weeks",
        lessons: 8
      }
    ],
    courses: [104, 105],
    certificate: true
  }
]

function LearningPath() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { enrolledCourses, user, enrollInCourse } = useStore()
  const [isEnrolled, setIsEnrolled] = useState(false)

  const path = learningPaths.find(p => p.id === parseInt(id))
  
  // Get course details for this path
  const pathCourses = path?.courses.map(courseId => 
    courses.find(c => c.id === courseId)
  ).filter(Boolean) || []

  useEffect(() => {
    if (path && user) {
      // Check if user is enrolled in any course in this path
      const enrolledInPath = path.courses.some(courseId => 
        enrolledCourses.includes(courseId)
      )
      setIsEnrolled(enrolledInPath)
    }
  }, [path, user, enrolledCourses])

  if (!path) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Learning Path Not Found</h2>
          <button 
            onClick={() => navigate('/learning-paths')}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors duration-200 font-medium"
          >
            Back to Learning Paths
          </button>
        </div>
      </div>
    )
  }

  const handleEnrollInPath = () => {
    if (!user) {
      localStorage.setItem('lastVisitedPage', `/learning-path/${id}`)
      navigate('/auth')
      return
    }
    
    // Enroll in all courses in the path
    path.courses.forEach(courseId => {
      if (!enrolledCourses.includes(courseId)) {
        enrollInCourse(courseId)
      }
    })
    
    setIsEnrolled(true)
  }

  const handleStartLearning = () => {
    if (!user) {
      localStorage.setItem('lastVisitedPage', `/learning-path/${id}`)
      navigate('/auth')
      return
    }
    
    // Navigate to first course in the path
    if (path.courses.length > 0) {
      navigate(`/course/${path.courses[0]}`)
    }
  }

  const handleSharePath = async () => {
    const shareData = {
      title: path.title,
      text: path.description,
      url: window.location.href,
    }
    
    if (navigator.share) {
      try {
        await navigator.share(shareData)
      } catch (err) {
        console.log('Error sharing:', err)
      }
    } else {
      navigator.clipboard.writeText(window.location.href)
      alert('Link copied to clipboard!')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className={`bg-gradient-to-br ${path.color} text-white py-16`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <button
                onClick={() => navigate('/learning-paths')}
                className="flex items-center space-x-2 text-white/80 hover:text-white mb-6 transition-colors duration-200"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Back to Learning Paths</span>
              </button>

              <div className="flex items-center space-x-3 mb-4">
                <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm">
                  {path.category}
                </span>
                <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm">
                  {path.courseCount} courses
                </span>
                <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm">
                  {path.level}
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-4">{path.title}</h1>
              <p className="text-xl text-white/90 mb-6">{path.description}</p>
              
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex items-center space-x-2">
                  <Clock className="w-5 h-5" />
                  <span>{path.totalDuration}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <BookOpen className="w-5 h-5" />
                  <span>{path.courseCount} courses</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="w-5 h-5" />
                  <span>{path.studentCount.toLocaleString()} students</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Star className="w-5 h-5 fill-current" />
                  <span>{path.rating}</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                {isEnrolled ? (
                  <button
                    onClick={handleStartLearning}
                    className="flex items-center space-x-3 px-8 py-4 bg-white text-gray-900 rounded-xl font-semibold hover:shadow-xl transition-all duration-200"
                  >
                    <Play className="w-6 h-6" />
                    <span>Continue Learning</span>
                  </button>
                ) : (
                  <button
                    onClick={handleEnrollInPath}
                    className="flex items-center space-x-3 px-8 py-4 bg-white text-gray-900 rounded-xl font-semibold hover:shadow-xl transition-all duration-200"
                  >
                    <BookOpen className="w-6 h-6" />
                    <span>Enroll in Path</span>
                  </button>
                )}
                <button
                  onClick={handleSharePath}
                  className="flex items-center space-x-3 px-8 py-4 bg-white/20 backdrop-blur-sm text-white rounded-xl font-semibold hover:bg-white/30 transition-all duration-200"
                >
                  <Share2 className="w-6 h-6" />
                  <span>Share Path</span>
                </button>
              </div>
            </div>

            <div className="relative">
              <img 
                src={path.image} 
                alt={path.title}
                className="rounded-2xl shadow-2xl w-full h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-2xl"></div>
              {path.certificate && (
                <div className="absolute bottom-4 left-4">
                  <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium flex items-center space-x-2">
                    <Award className="w-4 h-4" />
                    <span>Certificate Available</span>
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {/* Learning Outcomes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-md p-6 mb-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                <Target className="w-6 h-6 text-primary" />
                <span>What You'll Achieve</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {path.outcomes.map((outcome, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{outcome}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Curriculum Modules */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-md p-6 mb-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Path Curriculum</h2>
              <div className="space-y-4">
                {path.modules.map((module, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-4 mb-3">
                          <div className="w-10 h-10 bg-primary/10 text-primary rounded-xl flex items-center justify-center text-lg font-bold">
                            {index + 1}
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">{module.title}</h3>
                            <p className="text-gray-600">{module.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>{module.duration}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <BookOpen className="w-4 h-4" />
                            <span>{module.lessons} lessons</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Courses in this Path */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl shadow-md p-6"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Courses in this Path</h2>
              <div className="space-y-4">
                {pathCourses.map((course, index) => (
                  <motion.div
                    key={course.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`border rounded-xl p-6 hover:shadow-lg transition-all duration-300 ${
                      enrolledCourses.includes(course.id) 
                        ? 'border-primary bg-primary/5' 
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">
                            {index + 1}
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              course.skillLevel === 'Beginner' ? 'bg-green-100 text-green-700' :
                              course.skillLevel === 'Intermediate' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {course.skillLevel}
                            </span>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              course.tier === 'free' ? 'bg-green-100 text-green-800' :
                              course.tier === 'premium' ? 'bg-blue-100 text-blue-800' :
                              'bg-purple-100 text-purple-800'
                            }`}>
                              {course.tier === 'free' ? 'Free' : course.tier === 'premium' ? 'Plus' : 'Pro'}
                            </span>
                          </div>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{course.title}</h3>
                        <p className="text-gray-600 mb-4">{course.description}</p>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>{course.duration}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <BookOpen className="w-4 h-4" />
                            <span>{course.modules.length} modules</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Users className="w-4 h-4" />
                            <span>{course.students.toLocaleString()} students</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Star className="w-4 h-4 text-yellow-500 fill-current" />
                            <span>{course.rating}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col space-y-2 ml-4">
                        <button
                          onClick={() => {
                            if (!user) {
                              localStorage.setItem('lastVisitedPage', `/learning-path/${id}`)
                              navigate('/auth')
                              return
                            }
                            navigate(`/course/${course.id}`)
                          }}
                          className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                            enrolledCourses.includes(course.id)
                              ? 'bg-primary text-white hover:bg-primary/90'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {enrolledCourses.includes(course.id) ? (
                            <>
                              <Play className="w-4 h-4" />
                              <span>Continue</span>
                            </>
                          ) : (
                            <>
                              <BookOpen className="w-4 h-4" />
                              <span>View Course</span>
                            </>
                          )}
                        </button>
                        {course.hasCertificate && (
                          <button
                            onClick={() => {
                              if (!user) {
                                localStorage.setItem('lastVisitedPage', `/learning-path/${id}`)
                                navigate('/auth')
                                return
                              }
                              navigate(`/course/${course.id}#certificate`)
                            }}
                            className="flex items-center space-x-2 px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 transition-colors duration-200"
                          >
                            <Award className="w-4 h-4" />
                            <span>Certificate</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Path Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-md p-6"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-4">Path Overview</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Courses</span>
                  <span className="font-semibold">{path.courseCount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Duration</span>
                  <span className="font-semibold">{path.totalDuration}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Skill Level</span>
                  <span className="font-semibold">{path.level}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Students Enrolled</span>
                  <span className="font-semibold">{path.studentCount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Rating</span>
                  <div className="flex items-center space-x-1">
                    <Star className="w-4 h-4 text-yellow-500 fill-current" />
                    <span className="font-semibold">{path.rating}</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Prerequisites */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl shadow-md p-6"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                <span>Prerequisites</span>
              </h3>
              <ul className="space-y-3">
                {path.prerequisites.map((prereq, index) => (
                  <li key={index} className="flex items-start space-x-3 text-gray-700">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span>{prereq}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Certificate Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-gradient-to-br from-primary to-accent rounded-xl p-6 text-white"
            >
              <div className="flex items-center space-x-3 mb-3">
                <Award className="w-6 h-6" />
                <h3 className="text-lg font-bold">Path Certificate</h3>
              </div>
              <p className="text-white/90 text-sm mb-4">
                Complete all courses in this learning path to earn a verified certificate that demonstrates your mastery of the topic.
              </p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-sm text-white/80">
                  <CheckCircle className="w-4 h-4" />
                  <span>Verified upon completion</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-white/80">
                  <CheckCircle className="w-4 h-4" />
                  <span>Shareable with employers</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-white/80">
                  <CheckCircle className="w-4 h-4" />
                  <span>LinkedIn integration</span>
                </div>
              </div>
            </motion.div>

            {/* Related Paths */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white rounded-xl shadow-md p-6"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-4">Related Paths</h3>
              <div className="space-y-3">
                {learningPaths
                  .filter(p => p.id !== path.id && p.category === path.category)
                  .slice(0, 2)
                  .map(relatedPath => (
                    <button
                      key={relatedPath.id}
                      onClick={() => navigate(`/learning-path/${relatedPath.id}`)}
                      className="w-full text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200 flex items-center justify-between"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{relatedPath.title}</div>
                        <div className="text-sm text-gray-600">{relatedPath.courseCount} courses</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </button>
                  ))}
                <button
                  onClick={() => navigate('/learning-paths')}
                  className="w-full text-center py-2 text-primary font-medium hover:text-primary/80 transition-colors duration-200"
                >
                  View All Paths →
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LearningPath